/**
 * Catalog of buyer-selectable payment methods.
 *
 * IDs are the canonical strings stored in `CheckoutPageNode.payment_methods`
 * and matched by the buyer-frontend renderer + Cost+ HPP order creation.
 * Adding a new method here requires a matching renderer entry in
 * frontend/src/render/blocks.ts and a Cost+ HPP method-code mapping when
 * the orchestrator wires it.
 */

export interface PaymentMethodEntry {
  id: string;
  label: string;
  /** Short note shown under the label in the picker. */
  hint?: string;
  /** Region tags — purely informational in the picker, no behavior tied to it. */
  regions: string[];
  /** Whether the buyer-frontend treats this as an "express" method (separate row above the form). */
  express?: boolean;
}

export const PAYMENT_METHODS: readonly PaymentMethodEntry[] = [
  {
    id: "credit-card",
    label: "Credit / Debit cards",
    hint: "Visa, Mastercard, American Express",
    regions: ["Global"],
  },
  {
    id: "apple_pay",
    label: "Apple Pay",
    hint: "Express button on supported devices",
    regions: ["Global"],
    express: true,
  },
  {
    id: "google_pay",
    label: "Google Pay",
    hint: "Express button on supported devices",
    regions: ["Global"],
    express: true,
  },
  {
    id: "swish",
    label: "Swish",
    hint: "Mobile-bank payments — Sweden",
    regions: ["SE"],
  },
  {
    id: "vipps",
    label: "Vipps",
    hint: "Mobile wallet — Norway",
    regions: ["NO"],
  },
  {
    id: "mobilepay",
    label: "MobilePay",
    hint: "Mobile wallet — Denmark, Finland",
    regions: ["DK", "FI"],
  },
  {
    id: "vipps_mobilepay",
    label: "Vipps MobilePay",
    hint: "Combined Vipps + MobilePay flow (Nordic merger)",
    regions: ["NO", "DK", "FI"],
  },
];

export const PAYMENT_METHOD_BY_ID: Readonly<Record<string, PaymentMethodEntry>> =
  Object.fromEntries(PAYMENT_METHODS.map((m) => [m.id, m]));

export function paymentMethodLabel(id: string): string {
  return PAYMENT_METHOD_BY_ID[id]?.label ?? id;
}
